import { useState, useEffect } from 'react';
import useApi from '../../api';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Search, ListChecks, Clock } from 'lucide-react';
import { formatRelativeTime } from '../../utils/dateFormatter';

interface TaskDefinitionItem {
  id: string;
  name: string;
  system_name: string;
  instruction: string;
  created_at: string | null;
  updated_at: string | null;
}

interface TaskDefinitionsListProps {
  agentId: string;
  setShowTaskDefinitions: (show: boolean) => void;
  onAssociated: () => void;
}

export default function TaskDefinitionsList({
  agentId,
  setShowTaskDefinitions,
  onAssociated,
}: TaskDefinitionsListProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [taskDefinitions, setTaskDefinitions] = useState<TaskDefinitionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTaskDefinitions, setSelectedTaskDefinitions] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [associating, setAssociating] = useState(false);
  const { get, post } = useApi();

  function closeModal() {
    setIsOpen(false);
    setShowTaskDefinitions(false);
  }

  function toggleSelection(taskDefinitionId: string) {
    setSelectedTaskDefinitions((prev) => {
      const updated = new Set(prev);
      if (updated.has(taskDefinitionId)) {
        updated.delete(taskDefinitionId);
      } else {
        updated.add(taskDefinitionId);
      }
      return updated;
    });
  }

  async function associateTaskDefinitionsWithAgent() {
    const selectedTaskDefinitionIds = Array.from(selectedTaskDefinitions);
    setAssociating(true);

    try {
      await post(`/agents/${agentId}/task-definitions`, {
        task_definition_ids: selectedTaskDefinitionIds,
      });
      toast.success('Task definitions associated successfully', toastConfig);
      onAssociated();
      closeModal();
    } catch (error) {
      console.error('Error associating task definitions:', error);
      toast.error('Error associating task definitions', toastConfig);
    } finally {
      setAssociating(false);
    }
  }

  useEffect(() => {
    if (!isOpen) return;

    const url = agentId ? `/task-definitions/?exclude_agent_id=${agentId}` : '/task-definitions/';
    get(url)
      .then((response) => {
        setTaskDefinitions(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching task definitions:', error);
        setLoading(false);
      });
  }, [isOpen, agentId, get]);

  const filteredTaskDefinitions = taskDefinitions.filter((taskDefinition) => {
    const query = searchTerm.toLowerCase();
    return (
      taskDefinition.name.toLowerCase().includes(query) ||
      taskDefinition.system_name.toLowerCase().includes(query)
    );
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="sm:max-w-lg overflow-hidden">
        <DialogHeader>
            <div>
              <DialogTitle>Add existing task definition</DialogTitle>
              <DialogDescription className="mt-0.5">
                Select task definitions from global knowledge to associate with this agent.
              </DialogDescription>
            </div>
        </DialogHeader>

        <div className="space-y-3 min-w-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search task definitions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {selectedTaskDefinitions.size > 0 && (
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs font-medium text-muted-foreground">
                {selectedTaskDefinitions.size} selected
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-6"
                onClick={() => setSelectedTaskDefinitions(new Set())}
              >
                Clear
              </Button>
            </div>
          )}

          <div className="h-[320px] rounded-lg border overflow-hidden">
            <div className="h-full overflow-y-auto">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin h-5 w-5 border-2 border-muted-foreground rounded-full border-t-transparent" />
                  <p className="text-sm text-muted-foreground mt-3">Loading task definitions...</p>
                </div>
              ) : filteredTaskDefinitions.length > 0 ? (
                <div className="divide-y">
                  {filteredTaskDefinitions.map((taskDefinition) => (
                    <label
                      key={taskDefinition.id}
                      htmlFor={`td-${taskDefinition.id}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors overflow-hidden"
                    >
                      <Checkbox
                        id={`td-${taskDefinition.id}`}
                        checked={selectedTaskDefinitions.has(taskDefinition.id)}
                        onCheckedChange={() => toggleSelection(taskDefinition.id)}
                        className="shrink-0"
                      />
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <p className="text-sm font-medium truncate">{taskDefinition.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-muted-foreground font-mono truncate">
                            {taskDefinition.system_name}
                          </span>
                          {taskDefinition.updated_at && (
                            <span className="text-xs text-muted-foreground flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatRelativeTime(taskDefinition.updated_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                    <ListChecks className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground mt-3">
                    {searchTerm ? 'No matching task definitions' : 'No task definitions available'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {searchTerm
                      ? 'Try a different search term.'
                      : 'Record a task first, then you can share it across agents.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            disabled={selectedTaskDefinitions.size === 0 || associating}
            onClick={associateTaskDefinitionsWithAgent}
          >
            {associating ? 'Adding...' : `Add ${selectedTaskDefinitions.size} Task Definition${selectedTaskDefinitions.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
